import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { reportingLeLensAccessRequestPath, reportingLeLensAccessRequestsPath } from '~/consts/reportingLeLensApi';

import { request } from '~/services/common';

import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

export type LensAccessRequestState = { manager: string; requested: boolean };
export type LensOpenAccessRequest = { email: string; at: string };

export const lensAccessRequestQueryKeys = {
  mine: (lensId: string) => ['reporting', 'le-lens-access-request', lensId] as const,
  open: (lensId: string) => ['reporting', 'le-lens-access-requests', lensId] as const,
};

export function useGetLensAccessRequest(lensId: string, enabled = true) {
  return useQuery({
    queryKey: lensAccessRequestQueryKeys.mine(lensId),
    enabled,
    queryFn: async ({ signal }) =>
      (await request(resolveReportingLeLensUrl(reportingLeLensAccessRequestPath(lensId)), {
        signal,
      })) as LensAccessRequestState,
    retry: false,
  });
}

export function useRequestLensAccess(lensId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async () =>
      (await request(resolveReportingLeLensUrl(reportingLeLensAccessRequestPath(lensId)), {
        method: 'POST',
      })) as LensAccessRequestState,
    onSuccess: (state) => queryClient.setQueryData(lensAccessRequestQueryKeys.mine(lensId), state),
    onError: (error: Error) => enqueueSnackbar(error.message || 'Something went wrong', { variant: 'error' }),
    retry: false,
  });
}

export function useGetLensAccessRequests(lensId: string, enabled = true) {
  return useQuery({
    queryKey: lensAccessRequestQueryKeys.open(lensId),
    enabled,
    queryFn: async ({ signal }) =>
      (
        (await request(resolveReportingLeLensUrl(reportingLeLensAccessRequestsPath(lensId)), {
          signal,
        })) as { requests: Array<LensOpenAccessRequest> }
      ).requests,
    retry: false,
  });
}

export function useDismissLensAccessRequest(lensId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async (email: string) =>
      (
        (await request(resolveReportingLeLensUrl(`${reportingLeLensAccessRequestsPath(lensId)}/dismiss`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        })) as { requests: Array<LensOpenAccessRequest> }
      ).requests,
    onSuccess: (requests) => queryClient.setQueryData(lensAccessRequestQueryKeys.open(lensId), requests),
    onError: (error: Error) => enqueueSnackbar(error.message || 'Something went wrong', { variant: 'error' }),
    retry: false,
  });
}
